// Stage 3 — Quality Check (FR-3.*, July-2 FR-J13–J16, July-7 FR-K12–K16). The queue is grouped
// by preprint under a COLLAPSIBLE title header (FR-K13); each reviewer card carries the full
// detail set — email, PubMed, nearest neighbor, institutional profile, justification, proposing
// student name + email (FR-K12). Fixable/Reject prompt for STUDENT-facing feedback and Double
// Check for a SENIOR-AE-facing internal note (FR-K14). Students without QC access get "My
// Quality Checks" — their proposals + AE feedback with Edit, Reply, and Fixable → Fixed (FR-K16).
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DOMAINS,
  REVIEWER_STATUSES,
  nextStates,
  type Action,
  type Domain,
  type ProposalView,
  type ReviewerStatus,
} from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, CardBody, EmptyState, Field, Input, Modal, Select, Spinner, statusTone } from "../../components/ui.js";
import { ReviewerProfilePanel } from "../preprints/ReviewerProfilePanel.js";

/** QC rows arrive enriched with the preprint title + selectedId for grouping (FR-J15/J16). */
type QcRow = ProposalView & { preprintTitle: string; selectedId: string | null };

// Trimmed dropdown (FR-J13): Tentative Invite deduped into Double Check; H-index gates,
// Incomplete, and the old Invite Backup are retired.
const STATUS_ACTIONS: Partial<Record<ReviewerStatus, Action>> = {
  Invite: "qcSetInvite",
  "Invite Backup Unchecked": "qcSetInviteBackup",
  "Double Check": "qcSetDoubleCheck",
  Reject: "qcSetReject",
  Fixable: "qcSetFixable",
  Unchecked: "qcSetUnchecked",
  Fixed: "markReviewerFixed",
  "Student Reject": "studentRejectCandidate",
};
const SETTABLE_STATUSES = Object.keys(STATUS_ACTIONS) as ReviewerStatus[];

// Comment popups per status (FR-K14): who reads the comment decides where it's stored.
const COMMENT_PROMPTS: Partial<Record<ReviewerStatus, string>> = {
  Fixable: "Feedback for the proposing student (required for Fixable):",
  Reject: "Feedback for the proposing student (required for Reject):",
  "Double Check": "Internal note for the senior AE (required for Double Check):",
};

/** Older imported comments carry literal `\n` sequences — render them as real line breaks. */
const unescapeNewlines = (s: string): string => s.replace(/\\n/g, "\n");

export function QualityCheckPortal(): JSX.Element {
  const can = useCan();
  // July-8 FR-L9: the QC queue is cross-domain for QC-capable users — no domain in the gate,
  // and "All domains" is the default view (domain is an optional filter, not a restriction).
  const [domain, setDomain] = useState<Domain | "">("");
  const allowed = can("qcOpenPortal", {});
  // Students (no QC-portal capability) get their own scoped view instead (FR-J14/L4).
  return allowed ? <QcQueue domain={domain} setDomain={setDomain} /> : <MyQualityChecks />;
}

// ── Full QC queue (AE / QC roles) ─────────────────────────────────────────────
function QcQueue({ domain, setDomain }: { domain: Domain | ""; setDomain: (d: Domain | "") => void }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const [status, setStatus] = useState<ReviewerStatus | "">("Unchecked");
  const [profileFor, setProfileFor] = useState<{ selectedId: string; title: string } | null>(null);

  const params = new URLSearchParams();
  if (domain) params.set("domain", domain);
  if (status) params.set("status", status);
  // Domain-scoped ability checks: `""` (All domains) must read as "no domain scope".
  const ctx = domain ? { domain } : {};
  const { data, isLoading, error } = useQuery({
    queryKey: qk.qc({ domain, status }),
    queryFn: () => api.get<QcRow[]>(`/quality-check?${params.toString()}`),
  });

  const setStatusM = useMutation({
    mutationFn: ({ id, status, comments }: { id: string; status: ReviewerStatus; comments?: string }) =>
      api.post(`/quality-check/${id}/status`, { status, comments }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qc"] }),
  });
  const confirmDouble = useMutation({
    mutationFn: (id: string) => api.post(`/quality-check/${id}/confirm-double-check`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qc"] }),
  });

  // Group by preprint, title prominent (FR-J15); each group collapses (FR-K13).
  const groups = useMemo(() => {
    const byPreprint = new Map<string, { title: string; selectedId: string | null; rows: QcRow[] }>();
    for (const p of data ?? []) {
      const key = p.preprintId ?? "unknown";
      const g = byPreprint.get(key) ?? { title: p.preprintTitle, selectedId: p.selectedId, rows: [] };
      g.rows.push(p);
      byPreprint.set(key, g);
    }
    return [...byPreprint.entries()];
  }, [data]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Quality Check</h1>
          <p className="text-sm text-slate-500">Grouped by preprint — review each proposed reviewer and set a status.</p>
        </div>
        <div className="flex gap-2">
          {/* FR-L9: domain is an optional filter — the queue itself is cross-domain. */}
          <Select value={domain} onChange={(e) => setDomain(e.target.value as Domain | "")}>
            <option value="">All domains</option>
            {DOMAINS.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </Select>
          <Select value={status} onChange={(e) => setStatus(e.target.value as ReviewerStatus | "")}>
            <option value="">All statuses</option>
            {REVIEWER_STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </div>
      </header>

      {isLoading ? (
        <Spinner label="Loading queue…" />
      ) : error ? (
        <EmptyState>Could not load the QC queue.</EmptyState>
      ) : groups.length === 0 ? (
        <EmptyState>No reviewers match this filter.</EmptyState>
      ) : (
        <div className="space-y-4">
          {groups.map(([preprintId, g]) => (
            // FR-K13 + July-8 FR-L7: collapsible group per preprint, COLLAPSED by default — the
            // AE scans a compact list of titles and expands only the one they're working.
            <details key={preprintId} className="rounded-lg border border-slate-200 bg-white shadow-sm">
              <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-t-lg px-4 py-3 hover:bg-slate-50">
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold">{g.title}</h2>
                  <p className="text-xs text-slate-500">
                    {g.rows.length} proposed reviewer{g.rows.length === 1 ? "" : "s"}
                  </p>
                </div>
                {g.selectedId && (
                  <Button
                    variant="secondary"
                    onClick={(e) => {
                      e.preventDefault(); // don't toggle the group
                      setProfileFor({ selectedId: g.selectedId!, title: g.title });
                    }}
                  >
                    Reviewer profile
                  </Button>
                )}
              </summary>
              <div className="space-y-2 border-t border-slate-100 p-4">
                {g.rows.map((p) => (
                  <QcProposalRow
                    key={p.id}
                    p={p}
                    onSetStatus={(status, comments) => setStatusM.mutate({ id: p.id, status, comments })}
                    onConfirmDouble={() => confirmDouble.mutate(p.id)}
                    canConfirmDouble={can("qcConfirmDoubleCheck", ctx)}
                    canForStatus={(t) =>
                      can(STATUS_ACTIONS[t]!, ctx) || (t === "Invite" && can("qcJuniorInvite", ctx))
                    }
                  />
                ))}
              </div>
            </details>
          ))}
        </div>
      )}

      {profileFor && (
        <Modal open onClose={() => setProfileFor(null)} title={`Reviewer profile — ${profileFor.title}`}>
          <ReviewerProfilePanel
            selectedId={profileFor.selectedId}
            canEdit={can("editSimilarArticles", ctx) || can("editPitchDeck", ctx)}
          />
        </Modal>
      )}
    </div>
  );
}

function QcProposalRow({
  p,
  onSetStatus,
  onConfirmDouble,
  canConfirmDouble,
  canForStatus,
}: {
  p: QcRow;
  onSetStatus: (status: ReviewerStatus, comments?: string) => void;
  onConfirmDouble: () => void;
  canConfirmDouble: boolean;
  canForStatus: (t: ReviewerStatus) => boolean;
}): JSX.Element {
  const valid = new Set(nextStates("reviewer", p.status));
  const options = SETTABLE_STATUSES.filter((t) => valid.has(t) && canForStatus(t));
  const pubmed = p.pubMedLink ?? p.linkToPublications;
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-slate-100 p-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge tone={statusTone(p.status)}>{p.status}</Badge>
          {p.hIndex != null && <Badge tone="violet">H {p.hIndex}</Badge>}
        </div>
        {/* FR-K12: the full reviewer card — the five things + the proposing student. */}
        <h3 className="mt-1 font-medium">
          {p.firstName} {p.lastName}
          {p.reviewerEmail && <span className="ml-2 text-xs font-normal text-slate-500">{p.reviewerEmail}</span>}
        </h3>
        <p className="text-xs text-slate-500">
          {p.subdiscipline ?? "—"} · {p.affiliation ?? "—"}
        </p>
        <div className="mt-0.5 flex flex-wrap gap-3 text-xs">
          {pubmed && (
            <a className="text-brand hover:underline" href={pubmed} target="_blank" rel="noreferrer">
              PubMed ↗
            </a>
          )}
          {p.linkToProfile && (
            <a className="text-brand hover:underline" href={p.linkToProfile} target="_blank" rel="noreferrer">
              Institutional profile ↗
            </a>
          )}
        </div>
        {p.bestMatchPaperTitle && (
          <p className="mt-1 text-xs text-slate-600">
            <span className="font-medium text-slate-500">Nearest neighbor:</span> {p.bestMatchPaperTitle}
          </p>
        )}
        {p.justification && <p className="mt-1 whitespace-pre-wrap text-xs italic text-slate-500">{p.justification}</p>}
        <p className="mt-1 text-xs text-slate-500">
          <span className="font-medium">Proposed by:</span> {p.studentName || "—"}
          {p.proposerEmail && <span className="ml-1">({p.proposerEmail})</span>}
        </p>
        {p.qcComments && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-amber-700">QC (student-facing): {unescapeNewlines(p.qcComments)}</p>
        )}
        {p.qcInternalNote && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-violet-700">Internal note (AE↔AE): {unescapeNewlines(p.qcInternalNote)}</p>
        )}
        {p.studentReply && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-sky-700">Student reply: {unescapeNewlines(p.studentReply)}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {p.status === "Double Check" && canConfirmDouble && <Button onClick={onConfirmDouble}>Confirm → Invite</Button>}
        {options.length > 0 && (
          <Select
            value=""
            aria-label="Set status"
            onChange={(e) => {
              const t = e.target.value as ReviewerStatus;
              if (!t) return;
              // FR-K14: Fixable + Reject collect student-facing feedback; Double Check collects
              // the senior-AE-facing note. All three are required.
              const prompt = COMMENT_PROMPTS[t];
              let comments: string | undefined;
              if (prompt) {
                comments = window.prompt(prompt) ?? "";
                if (!comments.trim()) return;
              }
              onSetStatus(t, comments);
            }}
          >
            <option value="">Set status…</option>
            {options.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        )}
      </div>
    </div>
  );
}

// ── Student "My Quality Checks" (FR-J14, July-7 FR-K16) ──────────────────────
function MyQualityChecks(): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<QcRow | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: qk.qc({ mine: "1" }),
    queryFn: () => api.get<QcRow[]>(`/quality-check/mine`),
  });
  const markFixed = useMutation({
    mutationFn: (id: string) => api.post(`/quality-check/${id}/fixed`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qc"] }),
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-bold">My Quality Checks</h1>
        <p className="text-sm text-slate-500">
          Your proposals the quality checkers have acted on (still-unchecked ones stay in Find Reviewers). When a
          reviewer is marked Fixable, edit the profile to apply the fix, reply to the checker, and mark it Fixed.
        </p>
      </header>
      {isLoading ? (
        <Spinner label="Loading your proposals…" />
      ) : error ? (
        <EmptyState>Could not load your proposals.</EmptyState>
      ) : (data ?? []).length === 0 ? (
        <EmptyState>You haven’t proposed any reviewers yet.</EmptyState>
      ) : (
        <div className="space-y-2">
          {(data ?? []).map((p) => (
            <Card key={p.id}>
              <CardBody className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge tone={statusTone(p.status)}>{p.status}</Badge>
                    {p.domain && <Badge tone="blue">{p.domain}</Badge>}
                  </div>
                  <h3 className="mt-1 font-medium">
                    {p.firstName} {p.lastName}
                  </h3>
                  <p className="text-xs text-slate-500">{p.preprintTitle}</p>
                  {p.qcComments && (
                    <p className="mt-1 whitespace-pre-wrap text-xs text-amber-700">
                      QC feedback: {unescapeNewlines(p.qcComments)}
                    </p>
                  )}
                  {p.studentReply && (
                    <p className="mt-1 whitespace-pre-wrap text-xs text-sky-700">Your reply: {unescapeNewlines(p.studentReply)}</p>
                  )}
                  <ReplyBox proposalId={p.id} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* FR-K16 / July-8 FR-L5: Fixable → Edit the profile → Mark Fixed. Other
                      checked statuses are read-only feedback. */}
                  {p.status === "Fixable" && (
                    <>
                      <Button variant="secondary" onClick={() => setEditing(p)}>
                        Edit
                      </Button>
                      <Button onClick={() => markFixed.mutate(p.id)} disabled={markFixed.isPending}>
                        Mark Fixed
                      </Button>
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      {editing && <EditProfileModal proposal={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** Student comment reply ("I've made the correction") — stored on the proposal (FR-K16). */
function ReplyBox({ proposalId }: { proposalId: string }): JSX.Element {
  const qc = useQueryClient();
  const [comment, setComment] = useState("");
  const reply = useMutation({
    mutationFn: () => api.post(`/quality-check/${proposalId}/reply`, { comment }),
    onSuccess: () => {
      setComment("");
      qc.invalidateQueries({ queryKey: ["qc"] });
    },
  });
  return (
    <form
      className="mt-2 flex max-w-md items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (comment.trim()) reply.mutate();
      }}
    >
      <Input
        placeholder="Reply to the checker (e.g. “I’ve made the correction”)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <Button type="submit" variant="secondary" disabled={!comment.trim() || reply.isPending}>
        {reply.isPending ? "Sending…" : "Reply"}
      </Button>
    </form>
  );
}

/** Edit the reviewer profile to apply a QC fix (FR-K16) — PATCH /quality-check/:id/profile. */
function EditProfileModal({ proposal, onClose }: { proposal: QcRow; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState({
    affiliation: proposal.affiliation ?? "",
    secondaryAffiliation: proposal.secondaryAffiliation ?? "",
    highestDegree: proposal.highestDegree ?? "",
    title: proposal.title ?? "",
    subdiscipline: proposal.subdiscipline ?? "",
    linkToProfile: proposal.linkToProfile ?? "",
    publicationLink: proposal.linkToPublications ?? proposal.pubMedLink ?? "",
    bestMatchPaperTitle: proposal.bestMatchPaperTitle ?? "",
  });
  const save = useMutation({
    mutationFn: () => api.patch(`/quality-check/${proposal.id}/profile`, f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qc"] });
      onClose();
    },
  });
  const set = (key: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [key]: e.target.value });
  return (
    <Modal open onClose={onClose} title={`Edit reviewer profile — ${proposal.firstName} ${proposal.lastName}`}>
      <div className="space-y-3">
        <Field label="Affiliation">
          <Input value={f.affiliation} onChange={set("affiliation")} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Secondary affiliation">
            <Input value={f.secondaryAffiliation} onChange={set("secondaryAffiliation")} />
          </Field>
          <Field label="Highest degree">
            <Input value={f.highestDegree} onChange={set("highestDegree")} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Title">
            <Input value={f.title} onChange={set("title")} />
          </Field>
          <Field label="Sub-discipline">
            <Input value={f.subdiscipline} onChange={set("subdiscipline")} />
          </Field>
        </div>
        <Field label="Institutional profile link">
          <Input value={f.linkToProfile} onChange={set("linkToProfile")} />
        </Field>
        <Field label="Publication / PubMed link">
          <Input value={f.publicationLink} onChange={set("publicationLink")} />
        </Field>
        <Field label="Best-match paper title">
          <Input value={f.bestMatchPaperTitle} onChange={set("bestMatchPaperTitle")} />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
